import React, { useContext, useEffect } from 'react'
import { Field, useFormState, useForm } from 'react-final-form'
import { Box, Typography } from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { PageNumberContext } from '../PageNumberContext'
import ChooseTypeCard from '../ChooseTypeCard'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

const validateSendAt = value => {
  const now = new Date()
  if (!value) return 'Send date is required'
  const sendAtInUTC = value ? new Date(value).toISOString() : null
  if (sendAtInUTC < now) return 'Cannot schedule in the past'
  return undefined
}

const parseDateTime = value => {
  if (!value) return null
  return dayjs(value).utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z'
}

const formatDateTime = value => {
  if (!value) return null

  const userTimezone = dayjs.tz.guess()
  const utcTime = dayjs.utc(value, 'YYYY-MM-DD HH:mm:ss')
  return utcTime.tz(userTimezone)
}

export default function FirstPageForm() {
  const { values } = useFormState()
  const form = useForm()
  const { setPage } = useContext(PageNumberContext)
  const isSmsTemplate = values?.metadata?.phoneNumber
  const type = values?.type

  useEffect(() => {
    if (values.sendAt && typeof values.sendAt === 'string') {
      if (values.sendAt.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        const isoFormat = dayjs.utc(values.sendAt, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss') + 'Z'
        form.change('sendAt', isoFormat)
      }
    }
  }, [values.sendAt, form])

  const cardMapper = {
    DEEP_FAKE: {
      type: 'DEEP_FAKE',
      title: 'Deepfake Call (AI-Generated)',
      description: 'Send a simulated phone call using a bot with an AI-generated voice and script',
      isAI: false,
      number: values?.metadata?.callerName && 1,
      isDisabled: false,
      onChange: () => setPage('DEEP_FAKE_TEMPLATE')
    },
    SLACK: {
      type: 'SLACK',
      title: 'Slack Message',
      description: 'Send a phishing test message to selected users via Slack',
      isAI: false,
      number: values?.metadata?.message && 1,
      onChange: () => {
        setPage('SLACK_TEMPLATE')
      }
    },
    SMS: {
      type: 'SMS',
      title: 'SMS Message',
      description: 'Send a phishing test message as a text to mobile phones',
      isAI: false,
      number: isSmsTemplate && 1,
      onChange: () => {
        setPage('SMS_TEMPLATE')
      },
      isDisabled: false
    },
    MANUAL_EMAIL: {
      type: 'MANUAL_EMAIL',
      title: 'Custom Email',
      description: 'Choose a template or create a phishing email manually from scratch',
      isAI: false,
      onChange: () => {
        setPage('EMAIL_TEMPLATE')
      },
      isDisabled: false
    },
    AUTOMATED_EMAIL: {
      type: 'AUTOMATED_EMAIL',
      title: 'Auto-Selected Email Template',
      description: 'AI selects the best existing template from our database based on previous user simulation results',
      isAI: true,
      onChange: () => {
        setPage('EMAIL_TEMPLATE')
      },
      isDisabled: false
    },
    AUTOMATED_AI_EMAIL: {
      type: 'AUTOMATED_AI_EMAIL',
      title: 'Fully AI-Generated Email',
      description: 'AI writes a phishing email from scratch based on past user mistakes and tools used in your company',
      isAI: true,
      onChange: () => {
        setPage('AUTOMATED_AI_EMAIL_TEMPLATE')
      },
      isDisabled: false
    }
  }

  return (
    <Box display={'flex'} flexDirection={'column'} rowGap={3}>
      <ChooseTypeCard {...cardMapper[type]} />
      <Typography color={'#76767F'} fontWeight={500} fontSize={16} fontFamily={'Plus Jakarta Sans'}>
        Send Template At
      </Typography>
      <Box display={'flex'} columnGap={2}>
        <Box width={350}>
          <Field
            name={'sendAt'}
            validate={validateSendAt}
            parse={parseDateTime}
            format={formatDateTime}
            render={({ input, meta }) => (
              <>
                <DateTimePicker
                  label='Send At'
                  disablePast={true}
                  value={input.value || null}
                  onChange={input.onChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!meta.error && meta.touched,
                      helperText: meta.touched && meta.error,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          fontSize: 16,
                          fontWeight: 400,
                          fontFamily: 'Plus Jakarta Sans',
                          borderRadius: '10px'
                        },
                        backgroundColor: '#fff'
                      }
                    }
                  }}
                />
              </>
            )}
          />
        </Box>
      </Box>
    </Box>
  )
}
